import { useEffect, useState, useCallback } from 'react';
import apiClient from '../../lib/api';

interface SkillOverrideRow {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  title: string | null;
  type_name: string | null;
  assigned_to_id: string | null;
  assigned_to_name: string | null;
  assigned_to_email: string | null;
  store_name: string | null;
  override_reason: string | null;
  override_applied_by: string | null;
  override_applied_at: string | null;
  created_by_name: string | null;
  created_by_email: string | null;
  missing_skills: unknown;
  force_outside_shift: boolean | null;
  force_without_clockin: boolean | null;
}

function todayISO(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function formatMissingSkills(value: unknown): string {
  if (!value) return '—';
  try {
    const arr: unknown = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? JSON.parse(value)
        : value;
    if (!Array.isArray(arr) || arr.length === 0) return '—';
    return arr
      .map((s: unknown) => {
        if (typeof s === 'string') return s;
        if (s && typeof s === 'object') {
          const o = s as { name?: string; label?: string; id?: string };
          return o.name || o.label || o.id || JSON.stringify(s);
        }
        return String(s);
      })
      .join(', ');
  } catch {
    return String(value);
  }
}

export default function SkillOverrideAuditPanel() {
  const [rows, setRows] = useState<SkillOverrideRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState<string>(todayISO(-30));
  const [end, setEnd] = useState<string>(todayISO(30));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (start) params.set('start', new Date(start + 'T00:00:00').toISOString());
      if (end) params.set('end', new Date(end + 'T23:59:59').toISOString());
      const r = await apiClient.get<SkillOverrideRow[]>(
        `/calendar/appointments/skill-overrides?${params.toString()}`
      );
      if (r.success && r.data) setRows(r.data);
      else setError(r.error || 'Errore nel caricamento');
    } catch (e) {
      setError((e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [end, start]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Audit override skill
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Appuntamenti salvati ignorando le skill mancanti, con motivazione e operatore.
          </p>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400">Da</label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400">A</label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
            />
          </div>
          <button
            onClick={load}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
          >
            Aggiorna
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Caricamento…</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="p-2 text-left">Data / Ora</th>
              <th className="p-2 text-left">Appuntamento</th>
              <th className="p-2 text-left">Risorsa</th>
              <th className="p-2 text-left">Negozio</th>
              <th className="p-2 text-left">Skill mancanti</th>
              <th className="p-2 text-left">Motivazione</th>
              <th className="p-2 text-left">Operatore</th>
              <th className="p-2 text-left">Altri override</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((r) => {
              const otherFlags = [
                r.force_outside_shift ? 'fuori turno' : null,
                r.force_without_clockin ? 'no timbratura' : null,
              ].filter(Boolean);
              return (
                <tr key={r.id}>
                  <td className="p-2 align-top whitespace-nowrap">
                    <div className="font-medium">
                      {new Date(r.start_time).toLocaleString('it-IT')}
                    </div>
                    <div className="text-xs text-gray-500">
                      → {new Date(r.end_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <div className="font-medium">{r.title || '—'}</div>
                    <div className="text-xs text-gray-500">{r.type_name || ''}</div>
                  </td>
                  <td className="p-2 align-top">
                    <div>{r.assigned_to_name?.trim() || '—'}</div>
                    <div className="text-xs text-gray-500">{r.assigned_to_email || ''}</div>
                  </td>
                  <td className="p-2 align-top">{r.store_name || '—'}</td>
                  <td className="p-2 align-top">{formatMissingSkills(r.missing_skills)}</td>
                  <td className="p-2 align-top max-w-md">
                    <div className="whitespace-pre-wrap break-words">
                      {r.override_reason || <span className="text-gray-400">— nessuna motivazione —</span>}
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <div>{r.created_by_name?.trim() || '—'}</div>
                    <div className="text-xs text-gray-500">{r.created_by_email || ''}</div>
                    {r.override_applied_at && (
                      <div className="text-xs text-gray-400">
                        {new Date(r.override_applied_at).toLocaleString('it-IT')}
                      </div>
                    )}
                  </td>
                  <td className="p-2 align-top">
                    {otherFlags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {otherFlags.map((f) => (
                          <span
                            key={f}
                            className="inline-block rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-500">
                  Nessun override skill nel periodo selezionato
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
